import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CalendarDays, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASON_COLORS = {
  "Sick Leave": { bg: "#FEE2E2", text: "#DC2626", border: "#FECACA" },
  "Annual Leave": { bg: "#DBEAFE", text: "#2563EB", border: "#BFDBFE" },
  Training: { bg: "#FEF3C7", text: "#D97706", border: "#FDE68A" },
  Personal: { bg: "#E0E7FF", text: "#4F46E5", border: "#C7D2FE" },
  Other: { bg: "#F4F4F5", text: "#71717A", border: "#E4E4E7" },
};

const monthName = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric" });

// Build a UTC-safe local date at midnight, no tz drift
const dayKey = (y, m, d) => new Date(y, m, d).toISOString().slice(0, 10);

const AbsenceCalendarDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [engineers, setEngineers] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/engineer-directory`);
      setEngineers(res.data || []);
    } catch (e) {
      toast.error("Failed to load engineers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const { days, todayKey } = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let d = 1; d <= daysInMonth; d++) arr.push({ d, key: dayKey(y, m, d) });
    const t = new Date();
    return { days: arr, todayKey: dayKey(t.getFullYear(), t.getMonth(), t.getDate()) };
  }, [cursor]);

  // Engineers with at least one absence spanning this month
  const engineersInScope = useMemo(() => {
    const firstKey = days[0]?.key;
    const lastKey = days[days.length - 1]?.key;
    return engineers.filter((e) => {
      const s = e.absence_start_date || "";
      const en = e.absence_end_date || "";
      if (!s || !en) return e.is_available === false; // manual unavailable → still show row
      // overlap check
      return !(en < firstKey || s > lastKey);
    });
  }, [engineers, days]);

  const isDayAbsent = (eng, key) => {
    const s = eng.absence_start_date || "";
    const en = eng.absence_end_date || "";
    if (s && en) return key >= s && key <= en;
    return false;
  };

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl border-[#E4E4E7] rounded-sm max-h-[90vh] overflow-y-auto"
        data-testid="absence-calendar-dialog"
      >
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <DialogTitle
            className="text-2xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
          >
            <CalendarDays className="w-6 h-6" />
            Absence Calendar
          </DialogTitle>
          <DialogDescription className="text-sm text-[#71717A]">
            Every engineer's scheduled leave in a single month view. Coverage gaps show at a glance.
          </DialogDescription>
        </DialogHeader>

        {/* Month controls */}
        <div className="flex items-center justify-between py-3 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={prevMonth}
              className="rounded-sm border-[#E4E4E7]"
              data-testid="calendar-prev-month"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-lg font-semibold text-[#09090B] min-w-[180px] text-center" data-testid="calendar-month-label">
              {monthName(cursor)}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={nextMonth}
              className="rounded-sm border-[#E4E4E7]"
              data-testid="calendar-next-month"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={goToday}
              className="rounded-sm text-[#71717A]"
              data-testid="calendar-today"
            >
              Today
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(REASON_COLORS).map(([reason, c]) => (
              <span
                key={reason}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm font-semibold border"
                style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}
              >
                {reason}
              </span>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#71717A]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : engineersInScope.length === 0 ? (
          <div className="py-10 text-center text-[#71717A]" data-testid="calendar-empty">
            No absences scheduled for {monthName(cursor)}.
          </div>
        ) : (
          <div className="overflow-x-auto py-3">
            <div
              className="inline-grid"
              style={{
                gridTemplateColumns: `160px repeat(${days.length}, minmax(24px, 1fr))`,
                gap: "2px",
              }}
              data-testid="calendar-grid"
            >
              {/* Header row */}
              <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] px-2 py-1">
                Engineer
              </div>
              {days.map((day) => (
                <div
                  key={`h-${day.d}`}
                  className={`text-[10px] text-center font-bold py-1 rounded-sm ${
                    day.key === todayKey
                      ? "bg-[#0F172A] text-white"
                      : "text-[#71717A] bg-[#F4F4F5]"
                  }`}
                >
                  {day.d}
                </div>
              ))}

              {/* Rows per engineer */}
              {engineersInScope.map((eng) => (
                <>
                  <div
                    key={`n-${eng.id}`}
                    className="text-sm font-semibold text-[#09090B] px-2 py-1.5 truncate border-r border-[#E4E4E7]"
                    title={eng.name}
                    data-testid={`calendar-eng-${eng.id}`}
                  >
                    {eng.name}
                  </div>
                  {days.map((day) => {
                    const absent = isDayAbsent(eng, day.key);
                    const reason = eng.absence_reason || "Other";
                    const c = REASON_COLORS[reason] || REASON_COLORS.Other;
                    return (
                      <div
                        key={`${eng.id}-${day.d}`}
                        className={`h-7 rounded-sm ${day.key === todayKey ? "ring-1 ring-[#0F172A]" : ""}`}
                        style={
                          absent
                            ? { backgroundColor: c.bg, borderTop: `2px solid ${c.text}` }
                            : { backgroundColor: "#FAFAFA" }
                        }
                        title={absent ? `${eng.name} • ${reason} • ${day.key}` : ""}
                        data-testid={absent ? `calendar-absent-${eng.id}-${day.d}` : undefined}
                      />
                    );
                  })}
                </>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2 text-xs text-[#71717A] border-t border-[#E4E4E7] pt-3">
          {engineersInScope.length} engineer{engineersInScope.length !== 1 ? "s" : ""} with absence records
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AbsenceCalendarDialog;
